// src/hooks/useApi.js
import { useState, useCallback } from "react";
import axios from "axios";
import { useToast } from "./use-toast";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: "https://api.geeth365.com/",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token refresh state (module-level — shared across all hook instances)
let isRefreshing = false;
let refreshQueue = []; // [{ resolve, reject }]

const drainQueue = (error, token) => {
  refreshQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  refreshQueue = [];
};

// Request interceptor to add auth token if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");

    // Debug: Check what token is being used
    // console.log("🔐 Token from localStorage:", token ? "Present" : "Missing");
    // console.log("🔐 Token value:", token);

    if (token) {
      // Force Bearer with capital B
      config.headers.Authorization = `Bearer ${token}`;
      // console.log("✅ Authorization header set:", config.headers.Authorization);
    } else {
      console.warn("⚠️ No token found in localStorage");
    }

    // console.log("🚀 API Request:", {
    //   url: config.url,
    //   method: config.method,
    //   headers: config.headers,
    // });

    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor — automatic token refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401, only once per request, skip the refresh call itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/v1/refresh")
    ) {
      // If a refresh is already in flight, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const currentToken = localStorage.getItem("access_token") || "";
        const { data } = await axios.get(
          "https://api.geeth365.com/auth/v1/refresh",
          {
            headers: { Authorization: `Bearer ${currentToken}` },
            withCredentials: true,
          }
        );

        const newToken = data.access_token;
        localStorage.setItem("access_token", newToken);
        apiClient.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        drainQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        drainQueue(refreshError, null);
        // Clear stale credentials so the UI shows login again
        ["access_token", "token_type", "user_first_name", "user_name",
          "user_id", "user_roles", "geeth365_user"].forEach((k) =>
          localStorage.removeItem(k)
        );
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    console.error("❌ API Error:", {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
    });
    return Promise.reject(error);
  }
);

// Rest of the useApi hook remains the same...
const useApi = (options = {}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const {
    method: defaultMethod = "POST",
    headers: defaultHeaders = {},
    showToast = true,
  } = options;

  const callApi = useCallback(
    async (url, requestOptions = {}) => {
      const {
        body,
        method = defaultMethod,
        headers = {},
        showToast: requestShowToast = showToast,
        ...otherOptions
      } = requestOptions;

      setLoading(true);
      setError(null);
      setData(null);

      try {
        const config = {
          url,
          method: method.toLowerCase(),
          headers: { ...defaultHeaders, ...headers },
          ...otherOptions,
        };

        // Add data based on method
        if (["post", "put", "patch"].includes(method.toLowerCase())) {
          config.data = body;
        } else if (["get", "delete"].includes(method.toLowerCase())) {
          config.params = body;
        }

        const response = await apiClient(config);
        setData(response.data);

        if (requestShowToast && response.data?.message) {
          toast({
            title: "Success",
            description: response.data.message,
            variant: "default",
          });
        }

        return { data: response.data, status: response.status };
      } catch (err) {
        const errorMessage =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Something went wrong";

        setError(errorMessage);

        if (requestShowToast) {
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
        }

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [defaultMethod, defaultHeaders, showToast, toast]
  );

  const api = {
    post: (url, body, options = {}) =>
      callApi(url, { method: "POST", body, ...options }),

    get: (url, params = {}, options = {}) =>
      callApi(url, { method: "GET", body: params, ...options }),

    put: (url, body, options = {}) =>
      callApi(url, { method: "PUT", body, ...options }),

    patch: (url, body, options = {}) =>
      callApi(url, { method: "PATCH", body, ...options }),

    delete: (url, params = {}, options = {}) =>
      callApi(url, { method: "DELETE", body: params, ...options }),
  };

  return {
    callApi,
    api,
    loading,
    error,
    data,
    setLoading,
    setError,
    setData,
  };
};

export default useApi;
